import { ZfBox, ZfLoadingSpinner, ZfStack } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { SETTINGS_DRAWER_WIDTH } from '../../../app/config/constants';
import { LIQUIDITY_CALCULATOR_CHART_HEIGHT_PX, LIQUIDITY_CALCULATOR_CHART_MIN_HEIGHT_PX } from '../../config/constants';
import { useLiquidityCalculationData, useLiquidityCalculatorStackedLayout } from '../../hooks';
import type { LiquidityCalculatorSettingsSection } from '../../interfaces';
import { LiquidityCalculatorSettingsDrawer } from '../liquidity-calculator-settings';
import { LiquidityCalculatorGraph } from './liquidity-calculator-graph';
import { LiquidityCalculatorTable } from './liquidity-calculator-table';
import { LiquidityNeedDetailsModal } from './liquidity-need-details-modal';

export const LiquidityCalculator: FunctionComponent = () => {
    const calculationData = useLiquidityCalculationData();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [expandedSection, setExpandedSection] = useState<LiquidityCalculatorSettingsSection>();
    const [isNeedDetailsOpen, setIsNeedDetailsOpen] = useState(false);

    const openSettingsDrawer = (section: LiquidityCalculatorSettingsSection) => {
        setExpandedSection(section);
        setIsDrawerOpen(true);
    };

    const closeSettingsDrawer = () => {
        setIsDrawerOpen(false);
        setExpandedSection(undefined);
    };

    const { layoutRef, isStackedLayout } = useLiquidityCalculatorStackedLayout({
        enabled: !calculationData.isLoading,
        forceStacked: isDrawerOpen,
    });

    if (calculationData.isLoading) return <ZfLoadingSpinner style={{ marginLeft: 'auto', marginRight: 'auto', marginTop: 'var(--padding-top-3xl)' }} />;

    return (
        <ZfStack direction="row" className="liquidity-calculator">
            <ZfStack
                ref={layoutRef}
                direction={isStackedLayout ? 'column' : 'row'}
                spacing="extra-large"
                style={{
                    flex: '1 1 0',
                    padding: 'var(--padding-vertical-xl)',
                    width: isDrawerOpen ? `calc(100% - ${SETTINGS_DRAWER_WIDTH}px)` : '100%',
                    height: '100%',
                    maxHeight: '100%',
                    minWidth: 0,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    opacity: calculationData.isUpdating ? 0.6 : 1,
                    transition: 'opacity 150ms ease-in-out',
                }}
            >
                <ZfBox style={{ width: '100%', flex: isStackedLayout ? '0 0 auto' : '1 1 0', minWidth: 0 }}>
                    <LiquidityCalculatorTable
                        calculationData={calculationData}
                        isStackedLayout={isStackedLayout}
                        onOpenSettingsDrawer={openSettingsDrawer}
                        onOpenNeedDetails={() => setIsNeedDetailsOpen(true)}
                    />
                </ZfBox>
                <ZfStack
                    direction="column"
                    style={{
                        flex: isStackedLayout ? '1 1 auto' : '1 1 0',
                        minWidth: 0,
                        width: '100%',
                        minHeight: `${LIQUIDITY_CALCULATOR_CHART_MIN_HEIGHT_PX}px`,
                        maxHeight: `${LIQUIDITY_CALCULATOR_CHART_HEIGHT_PX}px`,
                        height: isStackedLayout ? undefined : '100%',
                    }}
                >
                    <LiquidityCalculatorGraph calculationData={calculationData} />
                </ZfStack>
            </ZfStack>
            <LiquidityCalculatorSettingsDrawer isDrawerOpen={isDrawerOpen} onCloseDrawer={closeSettingsDrawer} expandedSection={expandedSection} />
            <LiquidityNeedDetailsModal
                show={isNeedDetailsOpen}
                calculationData={calculationData}
                onClose={() => setIsNeedDetailsOpen(false)}
                onEditLiquidityNeed={() => {
                    setIsNeedDetailsOpen(false);
                    openSettingsDrawer('cashflows');
                }}
            />
        </ZfStack>
    );
};
